<template>
  <main class="home">
    <header class="workspace-topbar">
      <a class="topbar-brand" href="#page-title" aria-label="返回 LabDemo 首页顶部">
        <span class="topbar-brand-mark" aria-hidden="true">Q</span>
        <span class="topbar-brand-copy">
          <strong>Qone Lab</strong>
          <small>Workspace</small>
        </span>
      </a>

      <div class="topbar-context">
        <span>首页导航 <i aria-hidden="true">/</i> HOME·00</span>
        <strong>LabDemo 工作台</strong>
      </div>

      <nav class="topbar-nav" aria-label="首页模块导航">
        <a
          href="#product-pro"
          :class="{ active: activeSection === 'product-pro' }"
          :aria-current="activeSection === 'product-pro' ? 'location' : null"
        ><span>01</span><strong>产品需求文档</strong></a>
        <a
          href="#research"
          :class="{ active: activeSection === 'research' }"
          :aria-current="activeSection === 'research' ? 'location' : null"
        ><span>02</span><strong>竞品分析文档</strong></a>
        <a
          href="#core"
          :class="{ active: activeSection === 'core' }"
          :aria-current="activeSection === 'core' ? 'location' : null"
        ><span>03</span><strong>核心产品流程</strong></a>
        <a
          href="#showcases"
          :class="{ active: activeSection === 'showcases' }"
          :aria-current="activeSection === 'showcases' ? 'location' : null"
        ><span>04</span><strong>空间与技术实验</strong></a>
        <a
          href="#knowledge"
          :class="{ active: activeSection === 'knowledge' }"
          :aria-current="activeSection === 'knowledge' ? 'location' : null"
        ><span>05</span><strong>行业知识库</strong></a>
      </nav>
    </header>

    <header class="hero" aria-labelledby="page-title">
      <div class="hero-layout">
        <div class="hero-copy">
          <p class="eyebrow">Lighting Design Playground</p>
          <h1 id="page-title">LabDemo</h1>
          <p class="subtitle">集中探索智能灯光、户型分析与空间可视化，把产品原型、专业文档、交互实验和研究成果放在同一个工作台。</p>
        </div>

        <dl class="hero-facts" aria-label="工作台内容概览">
          <div><dt>{{ formatCount(productProCards.length) }}</dt><dd>PRD 文档</dd></div>
          <div><dt>{{ formatCount(researchReportCount) }}</dt><dd>竞品分析</dd></div>
          <div><dt>{{ formatCount(coreEntryCount) }}</dt><dd>核心流程</dd></div>
          <div><dt>{{ formatCount(experimentCount) }}</dt><dd>空间与技术实验</dd></div>
          <div><dt>{{ formatCount(knowledgeCount) }}</dt><dd>行业知识库</dd></div>
        </dl>
      </div>

    </header>

    <section id="product-pro" class="section product-pro-section" aria-labelledby="product-pro-title">
      <header class="section-header">
        <div class="section-heading">
          <span class="section-index">01</span>
          <div>
            <p class="section-kicker">Product PRO</p>
            <h2 id="product-pro-title">产品需求文档</h2>
          </div>
        </div>
        <p class="section-intro">沉淀从产品定义到交付落地的专业材料，快速进入构住生活项目的业务全景与产品基线。</p>
      </header>

      <div class="product-pro-grid">
        <a
          v-for="card in productProCards"
          :key="card.href"
          :href="card.href"
          class="product-pro-card"
          target="_blank"
          rel="noopener noreferrer"
        >
          <div class="product-pro-topline">
            <span class="product-pro-tag">{{ card.tag }}</span>
            <span class="product-pro-code">{{ card.code }}</span>
          </div>
          <div class="product-pro-copy">
            <p>{{ card.project }}</p>
            <h3>{{ card.title }}</h3>
            <span>{{ card.desc }}</span>
          </div>
          <div class="product-pro-footer">
            <span>{{ card.scope }}</span>
            <strong>打开独立 HTML <b aria-hidden="true">↗</b></strong>
          </div>
        </a>
      </div>
    </section>

    <section id="research" class="section research-section" aria-labelledby="research-title">
      <header class="section-header research-header">
        <div class="section-heading">
          <span class="section-index">02</span>
          <div>
            <p class="section-kicker">Competitive intelligence</p>
            <h2 id="research-title">竞品分析文档</h2>
          </div>
        </div>
        <p class="section-intro">将外部市场证据转化为产品能力地图、差异化判断与可执行建议，并在工作台内以 Vue 页面直接浏览。</p>
      </header>

      <div class="research-grid">
        <router-link :to="fiveCompetitorEntry.to" class="report-card five-competitor-report-card">
          <div class="report-label">
            <span>Competitive landscape</span>
            <span>2026 · Vue</span>
          </div>
          <div class="report-copy">
            <p>智能家居设计软件 · 五家全景</p>
            <h3>智能家居设计软件竞品分析</h3>
            <span class="report-action">进入工作台页面 <b aria-hidden="true">↗</b></span>
          </div>
          <p class="report-summary">从「销售工具」到「经营平台」的赛道全景拆解：麦哲伦、米案、家乐联、闲余、智装大狮的二维定位、逐个深度拆解与 15 维横向对比，并给出可落地的战略建议。</p>
          <dl class="report-facts" aria-label="五竞品报告内容概览">
            <div><dt>05</dt><dd>直接竞品</dd></div>
            <div><dt>15</dt><dd>对比维度</dd></div>
            <div><dt>05</dt><dd>战略建议</dd></div>
          </dl>
        </router-link>

        <router-link :to="reportUrl" class="report-card">
          <div class="report-label">
            <span>Platform study</span>
            <span>2026</span>
          </div>
          <div class="report-copy">
            <p>智能家居方案设计平台</p>
            <h3>米案智能家居平台<br>竞品深度分析</h3>
            <span class="report-action">打开独立 HTML 报告 <b aria-hidden="true">↗</b></span>
          </div>
          <p class="report-summary">覆盖专业方案设计器、商品、报价、订单、库存与经营数据，梳理完整业务闭环和系统实战技巧。</p>
          <dl class="report-facts" aria-label="米案报告内容概览">
            <div><dt>12</dt><dd>亮点功能</dd></div>
            <div><dt>08</dt><dd>视频教程</dd></div>
            <div><dt>03</dt><dd>竞争壁垒</dd></div>
          </dl>
        </router-link>

        <router-link :to="gouzhuMarketUrl" class="report-card gouzhu-report-card">
          <div class="report-label">
            <span>Market intelligence</span>
            <span>2026</span>
          </div>
          <div class="report-copy">
            <p>家居供应链与消费者商城</p>
            <h3>构住生活<br>竞品与市场分析</h3>
            <span class="report-action">打开独立 HTML 报告 <b aria-hidden="true">↗</b></span>
          </div>
          <p class="report-summary">从市场信号、用户价值和竞品格局出发，明确构住生活的核心差异、商业模式、机会优先级与阶段性落地路径。</p>
          <dl class="report-facts" aria-label="构住生活报告内容概览">
            <div><dt>12</dt><dd>分析章节</dd></div>
            <div><dt>90</dt><dd>天 MVP 验证</dd></div>
            <div><dt>180</dt><dd>天演进路线</dd></div>
          </dl>
        </router-link>
      </div>
    </section>

    <section id="core" class="section core-section" aria-labelledby="core-title">
      <header class="section-header">
        <div class="section-heading">
          <span class="section-index">03</span>
          <div>
            <p class="section-kicker">Start here</p>
            <h2 id="core-title">核心产品流程</h2>
          </div>
        </div>
        <p class="section-intro">从方案生成、户型理解到灯具参数设计，优先体验最接近真实业务的三个工作流。</p>
      </header>

      <div class="core-grid">
        <router-link class="primary-card" :to="featuredCard.to">
          <div class="card-topline">
            <span class="card-tag">{{ featuredCard.tag }}</span>
            <span class="card-number">01 / 03</span>
          </div>
          <div class="primary-copy">
            <span class="card-symbol" aria-hidden="true">⌁</span>
            <h3>{{ featuredCard.title }}</h3>
            <p>{{ featuredCard.desc }}</p>
          </div>
          <span class="card-action">{{ featuredCard.action }} <b aria-hidden="true">↗</b></span>
        </router-link>

        <div class="core-secondary">
          <router-link v-for="(card, index) in coreCards" :key="card.to" :to="card.to" class="core-card">
            <div>
              <span class="card-tag">{{ card.tag }}</span>
              <h3>{{ card.title }}</h3>
              <p>{{ card.desc }}</p>
            </div>
            <span class="round-arrow" aria-hidden="true">↗</span>
            <span class="card-number">0{{ index + 2 }} / 03</span>
          </router-link>
        </div>
      </div>
    </section>

    <section id="showcases" class="section showcase-section" aria-labelledby="showcase-title">
      <header class="section-header">
        <div class="section-heading">
          <span class="section-index">04</span>
          <div>
            <p class="section-kicker">Explore the lab</p>
            <h2 id="showcase-title">空间与技术实验</h2>
          </div>
        </div>
        <p class="section-intro">按使用场景分组浏览，快速找到空间效果演示或底层技术验证页面。</p>
      </header>

      <div class="experiment-group">
        <div class="group-title">
          <h3>空间预览</h3>
          <span>Spatial previews · {{ spatialCards.length }}</span>
        </div>
        <div class="cards spatial-grid balanced-three-grid">
          <router-link v-for="card in spatialCards" :key="card.to" :to="card.to" class="card">
            <div class="card-meta">
              <span>{{ card.tag }}</span>
              <span class="card-code">{{ card.code }}</span>
            </div>
            <h4>{{ card.title }}</h4>
            <p>{{ card.desc }}</p>
            <span class="text-link">打开演示 <b aria-hidden="true">↗</b></span>
          </router-link>
        </div>
      </div>

      <div class="experiment-group technical-group">
        <div class="group-title">
          <h3>技术实验</h3>
          <span>Technical studies · {{ technicalCards.length }}</span>
        </div>
        <div class="cards technical-grid balanced-three-grid">
          <router-link v-for="card in technicalCards" :key="card.to" :to="card.to" class="card technical-card">
            <div class="card-meta">
              <span>{{ card.tag }}</span>
              <span class="card-code">{{ card.code }}</span>
            </div>
            <h4>{{ card.title }}</h4>
            <p>{{ card.desc }}</p>
            <span class="text-link">打开实验 <b aria-hidden="true">↗</b></span>
          </router-link>
        </div>
      </div>
    </section>

    <section id="knowledge" class="section knowledge-section" aria-labelledby="knowledge-title">
      <header class="section-header">
        <div class="section-heading">
          <span class="section-index">05</span>
          <div>
            <p class="section-kicker">Industry knowledge</p>
            <h2 id="knowledge-title">行业知识库</h2>
          </div>
        </div>
        <p class="section-intro">把照明行业的术语、灯具、布置规则、参数与设计理念沉淀为可检索的参考，供产品、开发、测试随时查阅。</p>
      </header>

      <div class="experiment-group">
        <div class="group-title">
          <h3>行业知识</h3>
          <span>Industry knowledge · {{ knowledgeCards.length }}</span>
        </div>
        <div class="cards knowledge-grid balanced-three-grid">
          <router-link v-for="card in knowledgeCards" :key="card.to" :to="card.to" class="card knowledge-card">
            <div class="card-meta">
              <span>{{ card.tag }}</span>
              <span class="card-code">{{ card.code }}</span>
            </div>
            <h4>{{ card.title }}</h4>
            <p>{{ card.desc }}</p>
            <span class="text-link">{{ card.action || '打开知识库' }} <b aria-hidden="true">↗</b></span>
          </router-link>
        </div>
      </div>
    </section>

    <footer class="footer">
      <span>Qone Lab · Product playground</span>
      <a href="#page-title">返回顶部 ↑</a>
    </footer>

    <a class="floating-back-top" href="#page-title" aria-label="回到页面顶部">
      <span aria-hidden="true">↑</span>
      <small>TOP</small>
    </a>
  </main>
</template>

<script>
import { getWorkspaceGroup, productProEntries } from '../config/workspaceNavigation'

const coreEntries = getWorkspaceGroup('core').entries
const spatialEntries = getWorkspaceGroup('spatial').entries
const technicalEntries = getWorkspaceGroup('technical').entries
const knowledgeEntries = getWorkspaceGroup('knowledge').entries
const researchEntries = getWorkspaceGroup('research').entries
const fiveCompetitorEntry = researchEntries[0]
const researchReportCount = 3

export default {
  name: 'HomeView',
  data() {
    return {
      activeSection: 'product-pro',
      reportUrl: '/research/mian-competitive',
      gouzhuMarketUrl: '/research/gouzhu-market',
      researchReportCount,
      fiveCompetitorEntry,
      coreEntryCount: coreEntries.length,
      experimentCount: spatialEntries.length + technicalEntries.length,
      knowledgeCount: knowledgeEntries.length,
      productProCards: productProEntries.map(entry => ({
        ...entry,
        href: `${process.env.BASE_URL}${entry.href}`
      })),
      featuredCard: coreEntries.find(entry => entry.featured),
      coreCards: coreEntries.filter(entry => !entry.featured),
      spatialCards: spatialEntries,
      technicalCards: technicalEntries,
      knowledgeCards: knowledgeEntries
    }
  },
  mounted() {
    this.updateActiveSection()
    window.addEventListener('scroll', this.updateActiveSection, { passive: true })
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.updateActiveSection)
  },
  beforeCreate() {
    document.title = 'LabDemo · Qone Lab Workspace'
  },
  methods: {
    formatCount(value) {
      return String(value).padStart(2, '0')
    },
    updateActiveSection() {
      const sectionIds = ['product-pro', 'research', 'core', 'showcases', 'knowledge']
      const threshold = window.scrollY + Math.min(160, window.innerHeight * 0.22)
      let currentSection = sectionIds[0]

      sectionIds.forEach(sectionId => {
        const section = document.getElementById(sectionId)
        if (!section) return

        const sectionTop = section.getBoundingClientRect().top + window.scrollY
        if (sectionTop <= threshold) currentSection = sectionId
      })

      this.activeSection = currentSection
    }
  }
}
</script>

<style scoped>
.home {
  --ink: #17262a;
  --muted: #667069;
  --paper: #f4eddf;
  --paper-deep: #e8dbc4;
  --cream: #fffaf0;
  --orange: #e85d2a;
  --amber: #e9a23b;
  --green: #285d59;
  min-height: 100vh;
  padding: 0;
  color: var(--ink);
  background:
    linear-gradient(rgba(23, 38, 42, 0.035) 1px, transparent 1px),
    radial-gradient(circle at 8% 4%, rgba(233, 162, 59, 0.22), transparent 24rem),
    radial-gradient(circle at 94% 76%, rgba(40, 93, 89, 0.15), transparent 28rem),
    linear-gradient(135deg, #f8f2e6 0%, var(--paper-deep) 100%);
  background-size: 100% 48px, auto, auto, auto;
}

.hero,
.section,
.footer {
  width: 100%;
  margin: 0;
  box-sizing: border-box;
}

.workspace-topbar {
  position: sticky;
  top: 0;
  z-index: 1000;
  display: grid;
  width: 100%;
  min-height: 68px;
  padding: 0 clamp(16px, 4vw, 72px);
  grid-template-columns: auto minmax(190px, 0.55fr) minmax(520px, 1fr);
  align-items: center;
  gap: clamp(18px, 2.6vw, 42px);
  color: var(--cream);
  background:
    linear-gradient(90deg, rgba(255, 255, 255, 0.035) 1px, transparent 1px),
    linear-gradient(135deg, #101d21, #172b2f 58%, #1e3839);
  background-size: 32px 100%, auto;
  border-bottom: 1px solid rgba(255, 250, 240, 0.12);
  box-shadow: 0 12px 28px rgba(8, 17, 19, 0.22);
}

.topbar-brand,
.topbar-nav a {
  color: inherit;
  text-decoration: none;
}

.topbar-brand {
  display: inline-flex;
  align-items: center;
  gap: 9px;
}

.topbar-brand-mark {
  display: grid;
  width: 34px;
  height: 34px;
  place-items: center;
  color: var(--ink);
  background: var(--amber);
  font-family: Georgia, 'Songti SC', serif;
  font-size: 1rem;
  font-weight: 800;
}

.topbar-brand-copy {
  display: grid;
  line-height: 1;
}

.topbar-brand-copy strong,
.topbar-context span,
.topbar-nav span {
  font-family: Consolas, 'Courier New', monospace;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.topbar-brand-copy strong {
  font-size: 0.72rem;
}

.topbar-brand-copy small {
  margin-top: 5px;
  color: rgba(255, 250, 240, 0.56);
  font-size: 0.61rem;
}

.topbar-context {
  min-width: 0;
  padding-left: clamp(18px, 2.4vw, 34px);
  border-left: 1px solid rgba(255, 250, 240, 0.14);
}

.topbar-context span {
  display: block;
  margin-bottom: 4px;
  color: var(--amber);
  font-size: 0.61rem;
}

.topbar-context i {
  padding-inline: 5px;
  color: rgba(255, 250, 240, 0.34);
  font-style: normal;
}

.topbar-context strong {
  display: block;
  overflow: hidden;
  font-size: 0.92rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.topbar-nav {
  display: flex;
  min-width: 0;
  align-self: stretch;
  flex-wrap: nowrap;
  justify-content: flex-end;
  border-inline: 1px solid rgba(255, 250, 240, 0.11);
}

.topbar-nav a {
  position: relative;
  display: flex;
  min-width: 0;
  min-height: 68px;
  padding: 0 clamp(10px, 1.2vw, 20px);
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: rgba(255, 250, 240, 0.72);
  background: transparent;
  white-space: nowrap;
  transition: color 180ms ease, background 180ms ease;
}

.topbar-nav a + a {
  border-left: 1px solid rgba(255, 250, 240, 0.1);
}

.topbar-nav a::after {
  content: '';
  position: absolute;
  right: clamp(12px, 1.4vw, 24px);
  bottom: 0;
  left: clamp(12px, 1.4vw, 24px);
  height: 3px;
  background: var(--amber);
  transform: scaleX(0);
  transform-origin: center;
  transition: transform 180ms ease;
}

.topbar-nav a:hover {
  color: var(--cream);
  background: rgba(255, 255, 255, 0.05);
}

.topbar-nav a:hover::after,
.topbar-nav a.active::after {
  transform: scaleX(1);
}

.topbar-nav a.active {
  color: var(--cream);
  background: linear-gradient(180deg, rgba(233, 162, 59, 0.12), rgba(233, 162, 59, 0.025));
}

.topbar-nav span {
  padding-right: 10px;
  border-right: 1px solid rgba(255, 250, 240, 0.14);
  color: var(--amber);
  font-size: 0.58rem;
  font-weight: 800;
}

.topbar-nav a.active span {
  border-right-color: rgba(233, 162, 59, 0.38);
}

.topbar-nav strong {
  overflow: hidden;
  font-size: 0.74rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hero {
  padding: 34px clamp(16px, 4vw, 72px) 42px;
  border-bottom: 1px solid rgba(23, 38, 42, 0.22);
}

.hero-layout,
.section-header,
.section-heading,
.card-topline,
.card-meta,
.report-label,
.footer {
  display: flex;
  align-items: center;
}

.hero-layout {
  align-items: end;
  justify-content: space-between;
  gap: 48px;
}

.hero-copy {
  max-width: 720px;
}

.eyebrow,
.section-kicker {
  margin-bottom: 8px;
  color: #9d592c;
  font-family: Consolas, 'Courier New', monospace;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.hero h1 {
  margin: 0 0 12px;
  scroll-margin-top: 96px;
  font-family: Georgia, 'Songti SC', serif;
  font-size: clamp(3.1rem, 7vw, 6.7rem);
  letter-spacing: -0.075em;
  line-height: 0.82;
}

.subtitle {
  max-width: 680px;
  color: #53615c;
  font-size: clamp(0.94rem, 1.5vw, 1.08rem);
  line-height: 1.72;
}

.hero-facts,
.report-facts {
  display: grid;
  margin: 0;
}

.hero-facts {
  grid-template-columns: repeat(5, auto);
  flex: none;
}

.hero-facts div {
  min-width: 88px;
  padding: 4px 18px;
  border-left: 1px solid rgba(23, 38, 42, 0.22);
}

.hero-facts dt,
.report-facts dt {
  color: var(--orange);
  font-family: Georgia, serif;
  font-size: 1.7rem;
  font-weight: 700;
  line-height: 1;
}

.hero-facts dd,
.report-facts dd {
  margin-top: 6px;
  color: var(--muted);
  font-size: 0.7rem;
  white-space: nowrap;
}

.section {
  padding: clamp(58px, 8vw, 96px) clamp(16px, 4vw, 72px);
  scroll-margin-top: 96px;
}

.core-section {
  padding-top: clamp(48px, 5vw, 68px);
}

.section + .section {
  border-top: 1px solid rgba(23, 38, 42, 0.2);
}

.section-header {
  align-items: end;
  justify-content: space-between;
  gap: 32px;
  margin-bottom: 30px;
}

.section-heading {
  align-items: flex-start;
  gap: 18px;
}

.section-index {
  min-width: 32px;
  padding-top: 5px;
  color: var(--orange);
  font-family: Consolas, 'Courier New', monospace;
  font-size: 0.76rem;
  font-weight: 900;
}

.section-header h2 {
  font-family: Georgia, 'Songti SC', serif;
  font-size: clamp(1.9rem, 3.5vw, 3rem);
  letter-spacing: -0.045em;
  line-height: 1;
}

.section-intro {
  max-width: 430px;
  color: var(--muted);
  font-size: 0.9rem;
  line-height: 1.65;
}

.core-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.55fr) minmax(300px, 0.8fr);
  gap: 16px;
}

.primary-card,
.core-card,
.card,
.product-pro-card,
.report-card {
  color: inherit;
  text-decoration: none;
}

.primary-card {
  position: relative;
  display: flex;
  min-height: 420px;
  overflow: hidden;
  padding: clamp(26px, 4vw, 46px);
  flex-direction: column;
  justify-content: space-between;
  color: var(--cream);
  background:
    linear-gradient(rgba(255, 255, 255, 0.055) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.055) 1px, transparent 1px),
    radial-gradient(circle at 83% 22%, rgba(40, 128, 119, 0.58), transparent 31%),
    #112025;
  background-size: 30px 30px, 30px 30px, auto, auto;
  box-shadow: 0 26px 70px rgba(31, 45, 46, 0.22);
}

.primary-card::after {
  content: '';
  position: absolute;
  right: -80px;
  bottom: -110px;
  width: 300px;
  height: 300px;
  border: 1px solid rgba(233, 162, 59, 0.38);
  border-radius: 50%;
  box-shadow: 0 0 0 42px rgba(233, 162, 59, 0.05), 0 0 0 84px rgba(233, 162, 59, 0.035);
}

.card-topline,
.card-meta,
.report-label {
  justify-content: space-between;
}

.card-tag,
.card-number,
.card-meta,
.report-label {
  font-family: Consolas, 'Courier New', monospace;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.primary-card .card-tag {
  color: var(--amber);
}

.primary-card .card-number {
  color: rgba(255, 250, 240, 0.58);
}

.primary-copy {
  position: relative;
  z-index: 1;
  max-width: 620px;
}

.card-symbol {
  display: grid;
  width: 48px;
  height: 48px;
  margin-bottom: 22px;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 50%;
  color: var(--amber);
  font-family: Georgia, serif;
  font-size: 1.7rem;
}

.primary-card h3 {
  max-width: 650px;
  margin-bottom: 14px;
  font-family: Georgia, 'Songti SC', serif;
  font-size: clamp(2rem, 4.5vw, 3.6rem);
  letter-spacing: -0.05em;
  line-height: 1.04;
}

.primary-card p {
  max-width: 580px;
  color: #c4ceca;
  line-height: 1.7;
}

.card-action,
.text-link,
.report-action {
  position: relative;
  z-index: 1;
  width: fit-content;
  font-weight: 800;
}

.card-action {
  padding-bottom: 5px;
  border-bottom: 2px solid var(--amber);
}

.core-secondary {
  display: grid;
  gap: 16px;
}

.core-card {
  position: relative;
  display: grid;
  min-height: 202px;
  padding: 27px;
  grid-template-columns: 1fr auto;
  overflow: hidden;
  background: rgba(255, 250, 240, 0.8);
  border: 1px solid rgba(58, 45, 30, 0.13);
  box-shadow: 0 18px 42px rgba(70, 54, 34, 0.1);
}

.core-card .card-tag {
  color: #a76031;
}

.core-card h3 {
  margin: 24px 0 8px;
  font-family: Georgia, 'Songti SC', serif;
  font-size: 1.55rem;
}

.core-card p,
.card p {
  color: var(--muted);
  font-size: 0.86rem;
  line-height: 1.6;
}

.round-arrow {
  display: grid;
  width: 40px;
  height: 40px;
  place-items: center;
  border: 1px solid rgba(23, 38, 42, 0.18);
  border-radius: 50%;
  transition: color 180ms ease, background 180ms ease;
}

.core-card .card-number {
  position: absolute;
  right: 26px;
  bottom: 22px;
  color: #a88c69;
}

.experiment-group + .experiment-group {
  margin-top: 44px;
}

.group-title {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(23, 38, 42, 0.18);
}

.group-title h3 {
  font-family: Georgia, 'Songti SC', serif;
  font-size: 1.25rem;
}

.group-title span {
  color: #876c4e;
  font-family: Consolas, 'Courier New', monospace;
  font-size: 0.66rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.cards {
  display: grid;
  gap: 14px;
}

.balanced-three-grid {
  grid-template-columns: repeat(6, minmax(0, 1fr));
}

.balanced-three-grid > .card {
  grid-column: span 2;
}

.balanced-three-grid > .card:nth-last-child(2):nth-child(3n + 1),
.balanced-three-grid > .card:last-child:nth-child(3n + 2) {
  grid-column: span 3;
}

.balanced-three-grid > .card:last-child:nth-child(3n + 1) {
  grid-column: 1 / -1;
}

.card {
  display: flex;
  min-height: 190px;
  padding: 22px;
  flex-direction: column;
  background: rgba(255, 250, 240, 0.72);
  border: 1px solid rgba(58, 45, 30, 0.12);
  box-shadow: 0 16px 38px rgba(70, 54, 34, 0.08);
  transition: transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease;
}

.card-meta {
  color: #a76031;
}

.card-code {
  color: #a69074;
}

.card h4 {
  margin: 27px 0 8px;
  font-family: Georgia, 'Songti SC', serif;
  font-size: 1.3rem;
}

.text-link {
  margin-top: auto;
  padding-top: 20px;
  color: var(--ink);
  font-size: 0.75rem;
}

.technical-card {
  min-height: 170px;
  background: rgba(229, 218, 197, 0.62);
}

.research-section {
  padding-bottom: clamp(66px, 9vw, 110px);
}

.research-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

.research-grid > :last-child:nth-child(odd),
.product-pro-grid > :last-child:nth-child(odd) {
  grid-column: 1 / -1;
}

.research-grid .report-card {
  min-height: 540px;
  grid-template-columns: 1fr;
  align-content: start;
  gap: 28px;
}

.research-grid .report-summary {
  align-self: start;
}

.research-grid .report-facts {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-self: end;
}

.report-card {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(240px, 0.7fr) auto;
  gap: clamp(28px, 5vw, 70px);
  overflow: hidden;
  padding: clamp(28px, 5vw, 52px);
  color: var(--cream);
  background:
    linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px),
    radial-gradient(circle at 88% 18%, rgba(45, 124, 117, 0.45), transparent 27%),
    #101b20;
  background-size: 28px 28px, 28px 28px, auto, auto;
  border-bottom: 6px solid var(--orange);
  box-shadow: 0 26px 68px rgba(31, 45, 46, 0.22);
}

.gouzhu-report-card {
  background:
    linear-gradient(rgba(255, 255, 255, 0.045) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.045) 1px, transparent 1px),
    radial-gradient(circle at 86% 18%, rgba(232, 93, 42, 0.42), transparent 29%),
    #273f3d;
  background-size: 28px 28px, 28px 28px, auto, auto;
}

.gouzhu-report-card .report-label {
  color: var(--cream);
  background: var(--orange);
}

.five-competitor-report-card {
  background:
    linear-gradient(rgba(255, 255, 255, 0.045) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.045) 1px, transparent 1px),
    radial-gradient(circle at 84% 16%, rgba(233, 162, 59, 0.4), transparent 31%),
    #1d2f31;
  background-size: 28px 28px, 28px 28px, auto, auto;
  border-bottom: 6px solid var(--amber);
}

.five-competitor-report-card .report-label {
  color: #142226;
  background: #ffd38a;
}

.report-label {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  padding: 10px 18px;
  color: #142226;
  background: var(--amber);
}

.report-copy {
  padding-top: 26px;
}

.report-copy > p {
  margin-bottom: 12px;
  color: var(--amber);
  font-family: Consolas, 'Courier New', monospace;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.report-card h3 {
  margin-bottom: 28px;
  font-family: Georgia, 'Songti SC', serif;
  font-size: clamp(1.8rem, 3.4vw, 3rem);
  letter-spacing: -0.045em;
  line-height: 1.12;
}

.report-action {
  padding-bottom: 5px;
  border-bottom: 2px solid var(--amber);
}

.report-summary {
  align-self: center;
  color: #bdc9c5;
  font-size: 0.88rem;
  line-height: 1.8;
}

.report-facts {
  grid-template-columns: 1fr;
  align-content: center;
  gap: 8px;
  min-width: 124px;
}

.report-facts div {
  padding: 11px 12px;
  border: 1px solid rgba(255, 255, 255, 0.16);
}

.report-facts dd {
  color: #c7d0cd;
}

.footer {
  justify-content: space-between;
  padding: 24px clamp(16px, 4vw, 72px) 34px;
  border-top: 1px solid rgba(23, 38, 42, 0.2);
  color: #80684e;
  font-family: Consolas, 'Courier New', monospace;
  font-size: 0.68rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.footer a {
  color: var(--ink);
  text-decoration: none;
}

.floating-back-top {
  position: fixed;
  right: clamp(16px, 2vw, 34px);
  bottom: clamp(18px, 3vw, 34px);
  z-index: 900;
  display: grid;
  width: 56px;
  height: 56px;
  place-items: center;
  align-content: center;
  gap: 1px;
  color: var(--cream);
  background: var(--ink);
  border: 1px solid rgba(255, 250, 240, 0.26);
  border-radius: 50%;
  box-shadow: 0 16px 34px rgba(14, 28, 31, 0.28);
  text-decoration: none;
  transition: color 180ms ease, background 180ms ease, transform 180ms ease;
}

.floating-back-top span {
  font-family: Georgia, serif;
  font-size: 1.15rem;
  line-height: 0.9;
}

.floating-back-top small {
  font-family: Consolas, 'Courier New', monospace;
  font-size: 0.48rem;
  font-weight: 800;
  letter-spacing: 0.12em;
}

.floating-back-top:hover {
  color: var(--ink);
  background: var(--amber);
  transform: translateY(-3px);
}

.primary-card,
.core-card,
.card,
.product-pro-card,
.report-card,
.topbar-brand,
.topbar-nav a,
.floating-back-top,
.footer a {
  outline: none;
}

.primary-card:focus-visible,
.core-card:focus-visible,
.card:focus-visible,
.product-pro-card:focus-visible,
.report-card:focus-visible,
.topbar-brand:focus-visible,
.topbar-nav a:focus-visible,
.floating-back-top:focus-visible,
.footer a:focus-visible {
  box-shadow: 0 0 0 3px var(--paper), 0 0 0 6px var(--orange);
}

.primary-card,
.core-card,
.report-card {
  transition: transform 200ms ease, box-shadow 200ms ease;
}

.primary-card:hover,
.core-card:hover,
.report-card:hover {
  transform: translateY(-4px);
}

.primary-card:hover,
.report-card:hover {
  box-shadow: 0 32px 80px rgba(31, 45, 46, 0.3);
}

.core-card:hover .round-arrow {
  color: var(--cream);
  background: var(--ink);
}

.product-pro-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.product-pro-card {
  position: relative;
  display: flex;
  min-height: 360px;
  overflow: hidden;
  padding: clamp(26px, 3.5vw, 40px);
  flex-direction: column;
  justify-content: space-between;
  color: var(--cream);
  background:
    linear-gradient(115deg, rgba(255, 255, 255, 0.08), transparent 48%),
    radial-gradient(circle at 85% 14%, rgba(255, 211, 128, 0.32), transparent 28%),
    #cc562c;
  border-top: 7px solid var(--amber);
  box-shadow: 0 22px 54px rgba(84, 45, 27, 0.2);
  transition: transform 200ms ease, box-shadow 200ms ease;
}

.product-pro-card::before {
  content: 'PRO';
  position: absolute;
  right: -0.06em;
  bottom: -0.26em;
  color: rgba(255, 250, 240, 0.09);
  font-family: Georgia, 'Songti SC', serif;
  font-size: clamp(7rem, 15vw, 12rem);
  font-weight: 800;
  letter-spacing: -0.1em;
  line-height: 1;
  pointer-events: none;
}

.product-pro-topline,
.product-pro-footer {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
}

.product-pro-tag,
.product-pro-code,
.product-pro-copy > p,
.product-pro-footer > span {
  font-family: Consolas, 'Courier New', monospace;
  font-size: 0.67rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.product-pro-tag {
  padding: 7px 10px;
  color: #71331f;
  background: var(--amber);
}

.product-pro-code,
.product-pro-footer > span {
  color: rgba(255, 250, 240, 0.62);
}

.product-pro-copy {
  position: relative;
  z-index: 1;
  max-width: 560px;
  padding-block: 42px;
}

.product-pro-copy > p {
  margin-bottom: 12px;
  color: #ffd38a;
}

.product-pro-copy h3 {
  max-width: 540px;
  margin-bottom: 16px;
  font-family: Georgia, 'Songti SC', serif;
  font-size: clamp(2rem, 4vw, 3.25rem);
  letter-spacing: -0.05em;
  line-height: 1.08;
}

.product-pro-copy > span {
  display: block;
  max-width: 500px;
  color: rgba(255, 250, 240, 0.78);
  font-size: 0.88rem;
  line-height: 1.75;
}

.product-pro-footer {
  padding-top: 18px;
  border-top: 1px solid rgba(255, 250, 240, 0.22);
}

.product-pro-footer strong {
  flex: none;
  padding-bottom: 4px;
  border-bottom: 2px solid var(--amber);
  font-size: 0.78rem;
}

.product-pro-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 30px 72px rgba(49, 39, 29, 0.28);
}

.card:hover {
  transform: translateY(-4px);
  border-color: rgba(232, 93, 42, 0.35);
  box-shadow: 0 22px 48px rgba(70, 54, 34, 0.14);
}

@media (max-width: 900px) {
  .workspace-topbar {
    grid-template-columns: auto minmax(0, 1fr);
    gap: 16px;
  }

  .topbar-context {
    display: none;
  }

  .topbar-nav a {
    padding-inline: 9px;
  }

  .topbar-nav strong {
    font-size: 0.68rem;
  }

  .hero-layout,
  .section-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .hero-facts {
    width: 100%;
  }

  .hero-facts div {
    width: 100%;
  }

  .section-intro {
    max-width: 620px;
    padding-left: 50px;
  }

  .core-grid,
  .product-pro-grid,
  .research-grid,
  .report-card {
    grid-template-columns: 1fr;
  }

  .research-grid > :last-child:nth-child(odd),
  .product-pro-grid > :last-child:nth-child(odd) {
    grid-column: auto;
  }

  .core-secondary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .balanced-three-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .balanced-three-grid.balanced-three-grid > .card:nth-child(n) {
    grid-column: auto;
  }

  .balanced-three-grid.balanced-three-grid > .card:last-child:nth-child(odd) {
    grid-column: 1 / -1;
  }

  .report-summary {
    max-width: 620px;
  }

  .report-facts {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 620px) {
  .workspace-topbar {
    min-height: 84px;
    padding-inline: 10px;
    padding-block: 8px;
    align-items: stretch;
    gap: 8px;
  }

  .topbar-brand-copy {
    display: none;
  }

  .topbar-brand-mark {
    width: 32px;
    height: 32px;
  }

  .topbar-brand {
    align-self: center;
  }

  .topbar-nav {
    display: flex;
    flex-wrap: wrap;
    overflow: visible;
  }

  .topbar-nav a {
    min-width: 0;
    min-height: 34px;
    padding: 4px 8px;
    justify-content: flex-start;
    white-space: nowrap;
  }

  .topbar-nav a + a {
    border-left: 0;
  }

  .topbar-nav a:nth-child(even) {
    border-left: 1px solid rgba(255, 250, 240, 0.1);
  }

  .topbar-nav a:nth-child(n + 3) {
    border-top: 1px solid rgba(255, 250, 240, 0.1);
  }

  .hero {
    padding-top: 26px;
  }

  .hero-layout {
    gap: 28px;
  }

  .hero h1 {
    font-size: clamp(3.35rem, 19vw, 4.8rem);
  }

  .subtitle {
    font-size: 0.92rem;
  }

  .hero-facts div {
    min-width: 0;
    padding: 3px 12px;
  }

  .hero-facts {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    row-gap: 14px;
  }

  .hero-facts div:nth-child(odd) {
    padding-left: 0;
    border-left: 0;
  }

  .hero-facts dt {
    font-size: 1.4rem;
  }

  .section {
    padding-block: 58px;
  }

  .section-header {
    gap: 18px;
    margin-bottom: 24px;
  }

  .section-heading {
    gap: 10px;
  }

  .section-header h2 {
    font-size: 2rem;
  }

  .section-intro {
    padding-left: 42px;
  }

  .primary-card {
    min-height: 410px;
    padding: 26px;
  }

  .primary-card h3 {
    font-size: 2.25rem;
  }

  .product-pro-card {
    min-height: 390px;
  }

  .product-pro-copy {
    padding-block: 36px;
  }

  .product-pro-footer {
    align-items: flex-start;
    flex-direction: column;
    gap: 10px;
  }

  .core-secondary,
  .spatial-grid,
  .technical-grid {
    grid-template-columns: 1fr;
  }

  .core-card {
    min-height: 190px;
  }

  .group-title {
    align-items: flex-start;
    flex-direction: column;
    gap: 5px;
  }

  .card {
    min-height: 170px;
  }

  .report-card {
    gap: 24px;
    padding: 50px 26px 30px;
  }

  .report-copy {
    padding-top: 4px;
  }

  .report-card h3 {
    font-size: 2rem;
  }

  .report-facts {
    gap: 6px;
  }

  .report-facts div {
    padding: 10px 8px;
  }

  .report-facts dt {
    font-size: 1.35rem;
  }

  .report-facts dd {
    white-space: normal;
  }

  .footer {
    align-items: flex-start;
    flex-direction: column;
    gap: 12px;
  }

  .floating-back-top {
    width: 48px;
    height: 48px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .primary-card,
  .core-card,
  .card,
  .product-pro-card,
  .report-card,
  .topbar-nav a,
  .floating-back-top,
  .round-arrow {
    transition: none;
  }

  .primary-card:hover,
  .core-card:hover,
  .card:hover,
  .product-pro-card:hover,
  .report-card:hover,
  .floating-back-top:hover {
    transform: none;
  }
}
</style>
